import { ArrowUpRightIcon, CheckIcon } from '@phosphor-icons/react';

import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import type { BrandKit } from '@/hooks/useBrandKits';
import { useBrandKits } from '@/hooks/useBrandKits';
import { MENU_ITEM_MOBILE, SECTION_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

const KIT_DEFAULT_VALUE = 'default';
const KIT_OFF_VALUE = 'off';
const KIT_ID_PREFIX = 'kit:';

export const BRAND_KIT_MENU_VALUES = {
  DEFAULT: KIT_DEFAULT_VALUE,
  OFF: KIT_OFF_VALUE,
  ID_PREFIX: KIT_ID_PREFIX,
} as const;

export function kitIdToMenuValue(kitId: string | null, off?: boolean): string {
  if (off) return KIT_OFF_VALUE;
  if (!kitId) return KIT_DEFAULT_VALUE;
  return `${KIT_ID_PREFIX}${kitId}`;
}

export function menuValueToKitId(value: string): { kitId: string | null; off?: boolean } | null {
  if (value === KIT_DEFAULT_VALUE) return { kitId: null };
  if (value === KIT_OFF_VALUE) return { kitId: null, off: true };
  if (value.startsWith(KIT_ID_PREFIX)) return { kitId: value.slice(KIT_ID_PREFIX.length) };
  return null;
}

export const BRAND_KIT_ROW_LABEL = 'text-sm md:text-caption font-normal text-foreground/85';

export function KitSwatch({
  color,
  kitId,
}: {
  color?: string | null;
  kitId?: string;
}): JSX.Element {
  return (
    <span
      data-testid={kitId ? `brand-kit-swatch-${kitId}` : undefined}
      className="size-3 shrink-0 rounded-full border border-border/60"
      style={{ backgroundColor: color ?? 'hsl(var(--muted))' }}
      aria-hidden="true"
    />
  );
}

export interface BrandKitMenuItemsProps {
  onSelectKit: (kit: BrandKit) => void;
  onManage: () => void;
  showLabel?: boolean;
  closeOnSelect?: boolean;
}

export function BrandKitMenuItems({
  onSelectKit,
  onManage,
  showLabel = false,
  closeOnSelect = false,
}: BrandKitMenuItemsProps): JSX.Element {
  const { selectableKits: kits, activeKit: hookActiveKit } = useBrandKits();
  const active = hookActiveKit?.id ?? null;

  /**
   * Multi-brand is invisible to someone who has never needed it. A user with
   * zero or one kit opening this menu used to see either nothing (0) or one
   * inert row plus a bare "Manage brand kits" link (1) — neither explains
   * that a second brand is even a thing, let alone why you'd want one. Below
   * two kits, the trailing row teaches instead of just linking out; at two
   * or more the user already knows, so it reverts to the plain link.
   */
  const teaches = kits.length <= 1;

  return (
    <>
      {showLabel && <DropdownMenuLabel className={SECTION_LABEL}>Brand kit</DropdownMenuLabel>}
      {kits.length === 0 ? (
        <div className="px-2 py-2">
          <p className={cn(BRAND_KIT_ROW_LABEL, 'font-medium')}>No brand yet</p>
          <p className="mt-0.5 text-detail text-muted-foreground/70">
            Your workspace keeps its own voice, logo, colors and board.
          </p>
        </div>
      ) : (
        kits.map((kit) => (
          <DropdownMenuItem
            key={kit.id}
            data-testid={`brand-kit-row-${kit.id}`}
            className={cn(MENU_ITEM_MOBILE, 'justify-between')}
            onSelect={(e) => {
              if (!closeOnSelect) e.preventDefault();
              onSelectKit(kit);
            }}
          >
            <span className="flex min-w-0 items-center gap-2">
              <KitSwatch color={kit.primary_color} kitId={kit.id} />
              <span className={cn(BRAND_KIT_ROW_LABEL, 'truncate')}>
                {kit.name}
                {kit.is_default && (
                  <span className="ml-1 text-detail text-muted-foreground/70">· default</span>
                )}
              </span>
            </span>
            {active === kit.id && (
              <CheckIcon
                data-testid="brand-kit-active-check"
                className="size-3.5 shrink-0 text-foreground"
                weight="regular"
              />
            )}
          </DropdownMenuItem>
        ))
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem
        data-testid="brand-kit-manage-row"
        className={cn(MENU_ITEM_MOBILE, 'items-start justify-between gap-2')}
        onSelect={(e) => {
          e.preventDefault();
          onManage();
        }}
      >
        {teaches ? (
          <span className="flex min-w-0 flex-col">
            <span className={BRAND_KIT_ROW_LABEL}>
              {kits.length === 0 ? 'Set up your brand' : 'Manage brand kit'}
            </span>
            <span className="truncate text-detail text-muted-foreground/70">
              {kits.length === 0
                ? 'Set up your brand kit'
                : 'Voice, logo and board for this workspace'}
            </span>
          </span>
        ) : (
          <span className={BRAND_KIT_ROW_LABEL}>Manage brand kits</span>
        )}
        <ArrowUpRightIcon
          className="mt-0.5 size-3.5 shrink-0 text-muted-foreground/70"
          weight="regular"
        />
      </DropdownMenuItem>
    </>
  );
}
